// x-form-controller.ts
import { Change, nothingHere } from "@adornis/base/change.js";
import { logger } from "@adornis/base/logging.js";
import "@adornis/base/utilTypes.js";
import "@adornis/baseql/entities/adornisEntity.js";
import { constructValue } from "@adornis/baseql/entities/construct.js";
import "@adornis/baseql/utils/queryGeneration.js";
import "@adornis/chemistry/controllers/chemistry-controller.js";
import { XButton } from "@adornis/chemistry/elements/components/x-button.js";
import { XSnackbar } from "@adornis/chemistry/elements/components/x-snackbar.js";
import "@adornis/formfield/form-field.js";
import { TranslationController } from "@adornis/translation-core/client/translation-controller.js";
import { MultipleValidationError } from "@adornis/validation/errors/MultipleValidationError.js";
import { ValidationError } from "@adornis/validation/errors/ValidationError.js";
import "@adornis/validation/validator.js";
import "lit";
import { Directive } from "lit/async-directive.js";
import { directive } from "lit/directive.js";
import { Observable, Subscription, fromEvent, takeUntil } from "rxjs";
var FieldDirective = class extends Directive {
  constructor(partInfo) {
    super(partInfo);
    const element = partInfo.element;
    if (!element || !element.value)
      throw new Error(
        `formfield directive can only be used as ElementPart and only on FormFields but was used on ${element.nodeName}`
      );
    this._element = element;
  }
  render(controller, key1, key2, key3, key4, ...keys) {
    if (!key1)
      throw new Error("Cannot use formfield directive without specifying at least one key");
    const allKeys = [key1, key2, key3, key4, ...keys].filter((key) => key !== void 0);
    controller.updateFormFieldFromWithinDirective(allKeys, this._element);
  }
};
var fieldDirective = directive(FieldDirective);
var DisabledDirective = class extends Directive {
  constructor(partInfo) {
    super(partInfo);
    const element = partInfo.element;
    if (!element)
      throw new Error("disabled directive can only be used as ElementPart");
    this._element = element;
  }
  render(controller) {
    controller.updateEnableOnChangeListFromWithinDirective(this._element);
  }
};
var disabledDirective = directive(DisabledDirective);
var FormController = class {
  /**
   * Controller to manage form fields.
   *
   * handles the following:
   *
   * - validation of the resulting entity, limited to the fields that are displayed
   * - success / error callbacks
   * - display of errors on the correct form field
   * - enabling and disabling of form fields via selection set
   * - passing of additional information to the validation functions
   *
   * Have a look at the options object for more details on how to set all of this up.
   *
   * The element with the field directive has to extend FormField (value BehaviorSubject).
   * If it doesn't, an error will be thrown.
   *
   * The document has to be instantiated from a class that extends AdornisEntity. Use its
   * fields and decorators to enforce validation rules.
   *
   * **Example:**
   *
   * ```ts
   * class MyElement extends ChemistryLitElement {
   *   private _formController = new FormController(this, new MyEntity({}), {
   *     success: console.log,
   *     error: (e: ValidationError) => XDialog.alert(e.message),
   *   });
   *
   *   override render() {
   *     return html`
   *       <x-input ${this._formController.field("myNestedField", "myField")}></x-input>
   *       <x-button -@click=${this._formController.validate()}> Speichern </x-button>
   *     `;
   *   }
   * }
   * ```
   *
   *  The controller also acepts an NarrowedEntity as document, which is provide type safety for the fields based on the selection set.
   *
   *  **Example:**
   * ```ts
   *  class MyElement extends ChemistryLitElement {
   *
   *  @property({ attribute: false }) genehmigungID = new RXController<string | undefined>(this, undefined);
   *
   *  private _entity = new RxController(this, this.genehmigungID.observable.pipe(switchMap(async genehmigungID => {
   *    return await getByID(BeschaeftigungsGenehmigung, genehmigungID)({foo: 1});
   *  })));
   *
   *  private _formController = new FormController(this, this._entity.value, {
   *   success: console.log,
   *  error: (e: ValidationError) => XDialog.alert(e.message),
   * });
   *
   * override render() {
   *  return html`
   *   <x-input ${this._formController.field("foo")}></x-input>
   *   <x-button -@click=${this._formController.validate()}> Speichern </x-button>
   *   <x-input ${this._formController.field("bar")}></x-input> // will not work beacuse bar is not in the selection set
   *   <x-button -@click=${this._formController.validate()}> Speichern </x-button>
   * `;
   * }
   * ```
   */
  constructor(host, document, options = {}) {
    this.options = options;
    this.formFields = /* @__PURE__ */ new Map();
    this.controlDisabledElements = /* @__PURE__ */ new Set();
    this._defaultValidationMeta = { validateOmitted: true, aggregateErrors: true };
    this._hasChanges = false;
    if (!this.options.error)
      this.options.error = (err) => {
        logger.error({ err }, "error: ");
        if (err instanceof MultipleValidationError && err.validationErrors[0]) {
          return XSnackbar.show(
            this.options.translateError?.(err.validationErrors[0].extensions.translationKey ?? "") || err.validationErrors[0].message
          );
        }
        return XSnackbar.show(this.options.translateError?.(err.extensions.translationKey ?? "") || err.message);
      };
    (this.host = host).addController(this);
    this.translationController = new TranslationController(this.host);
    if (document instanceof Observable) {
      document.pipe(takeUntil(this.host.disconnected)).subscribe((doc) => {
        this.document = doc;
        this._setFieldsFromDocument();
        this.host.requestUpdate();
      });
    } else {
      this.document = document;
    }
    if (options.enabledFields && options.disabledFields)
      throw new Error("Can only use enabledFields OR disabledFields, not both at the same time");
    if (options.enabledFields) {
      if (options.enabledFields instanceof Observable) {
        options.enabledFields.pipe(takeUntil(this.host.disconnected)).subscribe((enabledFs) => {
          if (!enabledFs)
            this.enabledFields = void 0;
          else
            this.enabledFields = _convertSelectionSetToDotSyntax(enabledFs);
          this.host.requestUpdate();
        });
      } else {
        this.enabledFields = _convertSelectionSetToDotSyntax(options.enabledFields);
      }
    } else if (options.disabledFields) {
      if (options.disabledFields instanceof Observable) {
        options.disabledFields.pipe(takeUntil(this.host.disconnected)).subscribe((disabledFs) => {
          if (!disabledFs)
            this.disabledFields = void 0;
          else
            this.disabledFields = _convertSelectionSetToDotSyntax(disabledFs);
          this.host.requestUpdate();
        });
      } else {
        this.disabledFields = _convertSelectionSetToDotSyntax(options.disabledFields);
      }
    }
  }
  get validator() {
    if (this.options.validator)
      return this.options.validator;
    if (!this.document)
      throw new Error("Cannot get validator from undefined document");
    return this.document.validator;
  }
  _setHasChanged(value) {
    this._hasChanges = value;
    this._updateDisabledElements();
  }
  hasChanges() {
    return this._hasChanges || [...this.formFields.values()].some((fields) => fields.some((f) => f.field.preview));
  }
  _updateDisabledElements() {
    const hasChanges = this.hasChanges();
    for (const el of this.controlDisabledElements) {
      if (hasChanges)
        el.removeAttribute("disabled");
      else
        el.setAttribute("disabled", "");
    }
  }
  /**
   * @deprecated stub for broken implements typing
   */
  hostConnected() {
  }
  /**
   * @deprecated stub for broken implements typing
   */
  hostDisconnected() {
    for (const formFields of this.formFields.values()) {
      for (const { subscription } of formFields)
        subscription.unsubscribe();
    }
    this.controlDisabledElements.clear();
  }
  hostUpdated() {
    this._cleanupDisconnectedInputs();
    this._setFieldsFromDocument();
  }
  _attachEventListenersToFormField(keys, formField, subscription) {
    subscription.add(
      fromEvent(formField, "value-changed").pipe(takeUntil(this.host.disconnected)).subscribe((e) => this._handleValueChanged(keys, formField, e.detail.value))
    );
    subscription.add(
      fromEvent(formField, "preview-changed").pipe(takeUntil(this.host.disconnected)).subscribe((e) => this._updateDisabledElements())
    );
    subscription.add(
      fromEvent(formField, "value-picked").pipe(takeUntil(this.host.disconnected)).subscribe((e) => this.pickValueForField(keys, e.detail.value))
    );
  }
  pickValueForField(keys, value) {
    this._applySingleChange(keys, value, { ...this._defaultValidationMeta, ...this.options.validationMeta?.() }, true);
    this._setHasChanged(true);
    if (!this.document)
      throw new Error("this state should never be reached");
    this.options.onValuePicked?.(keys, value, this.document);
  }
  async _handleValueChanged(keys, formField, value) {
    try {
      await this.validator.validatePath(this.document || {}, keys, {
        ...this._defaultValidationMeta,
        ...this.options.validationMeta?.()
      });
      formField.error = void 0;
      const resolvedValue = Change.resolvePath(this.document, keys);
      if (resolvedValue !== value)
        this._setHasChanged(true);
    } catch {
    }
  }
  _setFieldsFromDocument(formKey) {
    const formFields = formKey ? [[formKey, this.formFields.get(formKey)]].filter(([_, formFields2]) => !!formFields2) : Array.from(this.formFields.entries());
    formFields.forEach(([key, formFields2]) => {
      const value = Change.resolvePath(this.document, key.split("."));
      if (value === nothingHere)
        return;
      formFields2.forEach((formField) => {
        if (formField.field.value.value !== value)
          formField.field.value.next(value);
      });
    });
  }
  /**
   * @deprecated just for internal use
   */
  updateFormFieldFromWithinDirective(keyPath, formField) {
    const formKey = keyPath.join(".");
    const formSelectorKey = keyPath.filter((key) => typeof key !== "number").join(".");
    const hasFormKey = this.formFields.has(formKey);
    const fieldAlreadyKnown = hasFormKey && this.formFields.get(formKey).find((entry) => entry.field === formField);
    if (!fieldAlreadyKnown) {
      this._removeField(formField);
    }
    const subscription = new Subscription();
    if (!hasFormKey)
      this.formFields.set(formKey, [{ field: formField, subscription, path: keyPath }]);
    else if (!fieldAlreadyKnown)
      this.formFields.get(formKey).push({ field: formField, subscription, path: keyPath });
    const applyDisabledFilter = (selectionKey) => formSelectorKey === selectionKey || (selectionKey + ".").startsWith(formSelectorKey + ".");
    formField.disabled = !!this.disabledFields?.find(applyDisabledFilter) || !!this.enabledFields && !this.enabledFields.find(applyDisabledFilter);
    if (!fieldAlreadyKnown) {
      this._attachEventListenersToFormField(keyPath, formField, subscription);
      this._setFieldsFromDocument();
    } else
      this._setFieldsFromDocument(formKey);
  }
  /**
   * @deprecated just for internal use
   */
  updateEnableOnChangeListFromWithinDirective(element) {
    this.controlDisabledElements.add(element);
  }
  /**
   * Directive to control the disabled attribute of an element depending on the changed state of this FormController.
   * The Element will receive the disabled attribute if not set and it will be removed once a change is registered in one of the atteched form fields.
   * You can use this directive multiple times.
   *
   * Note: If the change is reverted, this wont reset the disabled attribute!
   */
  controlDisabled() {
    return disabledDirective(this);
  }
  /**
   * directive to mirror entity values to the form
   * @param keys key path to entity value you want to mirror to the form
   *
   * @example
   * ```html
   *  <x-input ${this.formController.field("myNestedField", "myField")}></x-input>
   * ```
   */
  field(key, key2, key3, ...keys) {
    return fieldDirective(this, key, key2, key3, ...keys);
  }
  makeNestedFieldDirective(key, key2, key3) {
    return (nkey, nkey2, nkey3, ...keys) => fieldDirective(this, ...[key, key2, key3].filter(Boolean), nkey, nkey2, nkey3, ...keys);
  }
  _getAllInputs() {
    return Array.from(this.formFields.values()).flat().map((entry) => entry.field);
  }
  _filterFields(filter) {
    const currentFields = Array.from(this.formFields.entries());
    for (const [key, fields] of currentFields) {
      const removed = [];
      const filteredFields = fields.filter((f) => {
        const toFilter = filter(f.field);
        if (!toFilter)
          removed.push(f);
        return toFilter;
      });
      for (const entry of removed)
        entry.subscription.unsubscribe();
      if (filteredFields.length) {
        this.formFields.set(key, filteredFields);
      } else {
        this.formFields.delete(key);
      }
    }
  }
  _removeField(field) {
    this._filterFields((f) => f !== field);
  }
  _cleanupDisconnectedInputs() {
    this._filterFields((f) => f.isConnected);
  }
  /**
   * Call validate to start entity validation, callbacks can be defined in the constructor of `FormController`
   *
   * - Gets all values from registered `FormField`s (by using the `formController.field` directive) and applies them to the document
   * - runs `preValidate` methods on `FormField`s
   * - runs the entity validator
   * - runs `postValidate` methods on `FormField`s
   * - if necessary handles errors from validation and writes error states on affected `FormField`s
   */
  // eslint-disable-next-line complexity
  async validate(options = {}) {
    if (!this.validator)
      throw new Error("validator is missing");
    options = { ...this._defaultValidationMeta, ...this.options.validationMeta?.(), ...options };
    const button = options.mouseEvent?.currentTarget;
    if (button && button instanceof XButton)
      button.loading = true;
    this.options.validating?.();
    this._cleanupDisconnectedInputs();
    const inputsToValidate = this._getAllInputs();
    this.clearAllErrors();
    try {
      await Promise.all(
        inputsToValidate.map((input) => {
          input.error = void 0;
          return input.preValidate?.();
        })
      );
      const rawDoc = await this._fillDocumentAndReturn(options);
      const entity = constructValue(rawDoc);
      if (!entity)
        throw new Error("cant validate non-existent document");
      await this.validator.validate(entity, options);
      await Promise.all(inputsToValidate.map((input) => input.postValidate?.()));
      await this.options.onSuccess?.(entity);
      this.options.success?.(entity);
      if (button && button instanceof XButton)
        button.loading = false;
      this._hasChanges = false;
      return entity;
    } catch (error) {
      if (!(error instanceof ValidationError || error instanceof MultipleValidationError))
        throw error;
      await Promise.all(inputsToValidate.map((input) => input.postValidate?.(true)));
      this._showError(error);
      this.options.error?.(error);
      if (button && button instanceof XButton)
        button.loading = false;
      return null;
    }
  }
  async _applySingleChange(keys, value, validationMeta, validate) {
    try {
      const resolvedValue = Change.resolvePath(this.document, keys);
      if (resolvedValue === nothingHere) {
        let current = this.document || {};
        keys.forEach((pathPart, i) => {
          if (i !== keys.length - 1 && (!(pathPart in current) || current[pathPart] === void 0 || current[pathPart] === null))
            current[pathPart] = {};
          current = current[pathPart];
        });
      }
      new Change(value, keys).applyToContext(this.document, true);
      this.formFields.get(keys.join("."))?.forEach(({ field }) => {
        if ((field.value.value ?? field.value) !== value) {
          if (field.value.next)
            field.value.next(value);
          else
            field.value = value;
          field.error = void 0;
        }
      });
      if (validate)
        await this.validator.validatePath(this.document || {}, keys, validationMeta);
    } catch (error) {
      if (error instanceof ValidationError) {
        this._showErrorForField(error);
        this.options.fieldError?.(error);
      } else {
        console.error("Error while applying single change: ", error);
      }
    }
  }
  _showErrorForField(validationError, scrollToError) {
    const wrongInput = this._getAllInputsByValidationError(validationError)?.[0];
    if (wrongInput) {
      const errorString = validationError.extensions?.translationKey || validationError.message;
      if (this.options.translateError)
        wrongInput.error = this.options.translateError(errorString);
      else {
        const translatedString = this.translationController.translate(errorString);
        if (translatedString === errorString)
          wrongInput.error = validationError.message;
        else
          wrongInput.error = translatedString;
      }
      if (scrollToError && !this.options.dontScroll)
        wrongInput.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }
  _showError(validationError) {
    const errors = validationError instanceof MultipleValidationError ? validationError.validationErrors : [validationError];
    for (let i = 0; i < errors.length; i++) {
      const error = errors[i];
      this._showErrorForField(error, i === 0);
    }
  }
  /**
   * Clear all errors of registered (by using the `formController.field` directive) `FormField`s
   */
  clearAllErrors() {
    this._getAllInputs().forEach((inp) => inp.error = void 0);
  }
  async _fillDocumentAndReturn(validationMeta) {
    if (!this.document) {
      console.warn("wanting to fill null document");
      return null;
    }
    for (const [key, formFields] of this.formFields.entries()) {
      for (const { field } of formFields) {
        await this._applySingleChange(key.split("."), field.value.value, validationMeta);
      }
    }
    return this.document;
  }
  _getAllInputsByValidationError(e) {
    return this.formFields.get(concatEntityPath((e.extensions?.path ?? []).concat([e.extensions.key])))?.map((entry) => entry.field) ?? [];
  }
};
var concatEntityPath = (entityPaths) => entityPaths.reduce((acc, curr) => `${acc}${acc.length ? "." : ""}${curr}`, "");
var _convertSelectionSetToDotSyntax = (selection) => {
  if (typeof selection !== "object")
    return [];
  return Object.keys(selection).map((selectionKey) => {
    if (selection[selectionKey]) {
      const subDotSyntax = _convertSelectionSetToDotSyntax(selection[selectionKey]);
      if (subDotSyntax.length)
        return subDotSyntax.map((x) => `${selectionKey}${x ? "." : ""}${x}`);
      return [selectionKey];
    }
    return [""];
  }).flat();
};
export {
  FormController
};
